var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { observable, action, makeObservable, runInAction, computed, reaction } from 'mobx';
import { set, unset, get } from 'lodash';
import { getAxios } from '../../backend';
import { EventName } from '@whatsper/texterchat-common';
import { API_PATH } from '../../constants';
export class StorageStore {
    constructor(rootStore) {
        Object.defineProperty(this, "rootStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "storageData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "isLoading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "error", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "initialStorageLoaded", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        /**
         * Get a value from the storage data.
         *
         * @note The caller is responsible for ensuring the requested type T
         * matches the actual data type stored under 'key'.
         *
         * @param key - The key to get the value from.
         * @param defaultValue - The default value to return if the key is not found.
         * @returns The value from the storage data, or the default value if the key is not found.
         */
        Object.defineProperty(this, "getStorageValue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (key, defaultValue) => {
                if (!this.storageData) {
                    return defaultValue;
                }
                const value = get(this.storageData, key, defaultValue);
                return value;
            }
        });
        Object.defineProperty(this, "initializeStorage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (initialData) => {
                if (initialData) {
                    this.storageData = Object.assign({}, initialData);
                }
                else {
                    this.storageData = null;
                }
                this.isLoading = false;
                this.error = null;
            }
        });
        Object.defineProperty(this, "setStorageData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (key, value) => __awaiter(this, void 0, void 0, function* () {
                const userId = this.currentUserId;
                if (!userId) {
                    console.error('User ID not found for setStorageData');
                    runInAction(() => {
                        this.error = 'User not logged in';
                    });
                    return;
                }
                const previousStorageData = this.storageData;
                runInAction(() => {
                    this.isLoading = true;
                    this.error = null;
                    if (!this.storageData) {
                        this.storageData = {};
                    }
                    set(this.storageData, key, value);
                });
                try {
                    const axios = yield getAxios();
                    const payload = { key, operation: 'set', value };
                    yield axios.post(`${API_PATH.USERS}/${userId}/storage`, payload);
                    runInAction(() => {
                        this.isLoading = false;
                        this.storageData = Object.assign(Object.assign({}, this.storageData), { [key]: value });
                    });
                }
                catch (error) {
                    console.error('Failed to set storage data:', error);
                    runInAction(() => {
                        this.storageData = previousStorageData;
                        this.isLoading = false;
                        this.error = error.message || 'Failed to save data';
                    });
                }
            })
        });
        Object.defineProperty(this, "removeStorageData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (key) => __awaiter(this, void 0, void 0, function* () {
                const userId = this.currentUserId;
                if (!userId) {
                    console.error('User ID not found for removeStorageData');
                    runInAction(() => {
                        this.error = 'User not logged in';
                    });
                    return;
                }
                const previousStorageData = this.storageData;
                let keyExistedLocally = false;
                runInAction(() => {
                    this.isLoading = true;
                    this.error = null;
                    const tempData = Object.assign({}, this.storageData);
                    keyExistedLocally = unset(tempData, key);
                    if (keyExistedLocally && this.storageData) {
                        // Check storageData is not null
                        this.storageData = tempData;
                    }
                    else if (!keyExistedLocally) {
                        // If the key didn't exist, no change needed to this.storageData
                    }
                });
                if (!keyExistedLocally) {
                    console.warn(`Key "${key}" not found locally for removal.`);
                }
                try {
                    const axios = yield getAxios();
                    const payload = { key, operation: 'remove' };
                    yield axios.post(`${API_PATH.USERS}/${userId}/storage`, payload);
                    runInAction(() => {
                        this.isLoading = false;
                    });
                }
                catch (error) {
                    console.error('Failed to remove storage data:', error);
                    runInAction(() => {
                        // Revert only if we made a local change
                        if (keyExistedLocally) {
                            this.storageData = previousStorageData;
                        }
                        this.isLoading = false;
                        this.error = error.message || 'Failed to remove data';
                    });
                }
            })
        });
        Object.defineProperty(this, "onStorageUpdatedEvent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (data) => {
                if (data) {
                    const { key, prevValue } = data;
                    if (!this.storageData) {
                        this.storageData = {};
                    }
                    set(this.storageData, key, prevValue);
                }
                else {
                    console.warn('Received invalid storage update data via event:', data);
                }
            }
        });
        /**
         * Helper to set a specific key-value pair within a map object stored under a top-level key.
         * Ensures the map is treated immutably.
         */
        Object.defineProperty(this, "setItemInMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (mapKey, itemKey, itemValue) => __awaiter(this, void 0, void 0, function* () {
                const currentMap = this.getStorageValue(mapKey, {});
                const updatedMap = Object.assign(Object.assign({}, currentMap), { [itemKey]: itemValue });
                yield this.setStorageData(mapKey, updatedMap);
            })
        });
        /**
         * Helper to remove a specific key from a map object stored under a top-level key.
         * Ensures the map is treated immutably.
         */
        Object.defineProperty(this, "removeItemFromMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (mapKey, itemKey) => __awaiter(this, void 0, void 0, function* () {
                const currentMap = this.getStorageValue(mapKey, {});
                if (!(itemKey in (currentMap !== null && currentMap !== void 0 ? currentMap : {}))) {
                    console.log(`Key "${itemKey}" not found in map "${mapKey}", skipping removal.`);
                    return;
                }
                const _a = currentMap !== null && currentMap !== void 0 ? currentMap : {}, _b = itemKey, _ = _a[_b], updatedMap = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
                yield this.setStorageData(mapKey, updatedMap);
            })
        });
        this.rootStore = rootStore;
        makeObservable(this);
        this.rootStore.events.addListener(EventName.user_storage, (data) => this.onStorageUpdatedEvent(data));
        reaction(() => { var _a; return (_a = this.rootStore.loginStore) === null || _a === void 0 ? void 0 : _a.userDetails; }, (userDetails) => {
            // If user is logged in and storage is not loaded, initialize storage
            if (userDetails && !this.initialStorageLoaded) {
                console.log('User logged in, initializing storage');
                const initialStorage = userDetails === null || userDetails === void 0 ? void 0 : userDetails.storage;
                this.initializeStorage(initialStorage);
                runInAction(() => {
                    this.initialStorageLoaded = true;
                });
                // Cleanup on logout
            }
            else if (!userDetails && this.initialStorageLoaded) {
                console.log('User logged out, resetting storage');
                this.initializeStorage(null);
                runInAction(() => {
                    this.initialStorageLoaded = false;
                });
            }
        }, { fireImmediately: true });
    }
    get currentUserId() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.rootStore.loginStore) === null || _a === void 0 ? void 0 : _a.userDetails) === null || _b === void 0 ? void 0 : _b._id) !== null && _c !== void 0 ? _c : null;
    }
}
__decorate([
    observable
], StorageStore.prototype, "storageData", void 0);
__decorate([
    observable
], StorageStore.prototype, "isLoading", void 0);
__decorate([
    observable
], StorageStore.prototype, "error", void 0);
__decorate([
    observable
], StorageStore.prototype, "initialStorageLoaded", void 0);
__decorate([
    computed
], StorageStore.prototype, "currentUserId", null);
__decorate([
    action
], StorageStore.prototype, "initializeStorage", void 0);
__decorate([
    action
], StorageStore.prototype, "setStorageData", void 0);
__decorate([
    action
], StorageStore.prototype, "removeStorageData", void 0);
__decorate([
    action
], StorageStore.prototype, "onStorageUpdatedEvent", void 0);
__decorate([
    action
], StorageStore.prototype, "setItemInMap", void 0);
__decorate([
    action
], StorageStore.prototype, "removeItemFromMap", void 0);
